import { RestError } from '@azure/ms-rest-js';
import type { EventData as ErrorEventData } from '@ori-events/subscription-error';
import type { EventData } from '@ori-events/subscription-saved';
import { InfoCircle, PenFilled } from '@ori-ui/icons';
import { Button, IconButton, Link, styled, Tooltip, Typography } from '@ori-ui/mui';
import type { ApiModels } from '@ori/global-api-client';
import { useCallback, useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ModalContent } from '../../../shared/components';
import { localization } from '../../../shared/constants';
import { stateContext, wizardContext } from '../../../shared/contexts';
import { PriceReview, ReviewSection, TermsAndConditions } from '../../components';
import { useCustomerReview } from '../../hooks';
import { useAutoshipLegalArticles } from '../../hooks/useAutoshipLegalArticles';
import { useDeliveryAddress } from '../../hooks/useDeliveryAddress';
import { useRecurringPaymentCards } from '../../hooks/useRecurringPaymentCards';
import { Maestro, MasterCard, Other, Visa } from '../../icons';
import { CardNumber } from '~/AutoshipWizard/components/CardNumber';
import { useConfiguration } from '~/shared/contexts/configurationContext';
import { ECustomerTypeSelection, EWizardType } from '~/shared/types';
import type { ECardTypes } from '~/shared/types/ECardTypes';
import { client } from '~/shared/utils/fetcher';
import { getSafeUrl } from '~/shared/utils/getSafeUrl';
import { getToken } from '~/shared/utils/getToken';

const PrivacyPolicy = styled(Link)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));

const cardIcons = {
  Visa: <Visa fontSize="large" />,
  MasterCard: <MasterCard fontSize="large" />,
  Maestro: <Maestro fontSize="large" />,
  Other: <Other fontSize="large" />,
};

const resolveCardImage = (cardType: ECardTypes) => cardIcons[cardType as keyof typeof ECardTypes];

export function SubscriptionReviewPage() {
  const {
    selectedPackage,
    selectedFrequency,
    selectedCustomerType,
    termsAndConditionsAccepted,
    existingCustomer,
    newCustomer,
    setTermsAndConditions,
    setCurrentStep,
  } = useContext(stateContext);

  const { data: recurringPaymentCardsData } = useRecurringPaymentCards();
  const { data: customerAddresses } = useDeliveryAddress();
  const { data: termsAndPrivacyData } = useAutoshipLegalArticles();
  const content = useCustomerReview();
  const { onClose, onSave, onError, productCode } = useContext(wizardContext);
  const { customerId, globalApiUrl, identityUrl, placement, tenant, uriTemplate } =
    useConfiguration();
  const [isDisabled, setIsDisabled] = useState(false);
  const { formatMessage } = useIntl();

  const customerIdToPost =
    selectedCustomerType === ECustomerTypeSelection.ExistingCustomer
      ? existingCustomer?.endConsumerId
      : null;

  const token = getToken({ tenant, identityUrl });

  const customerAddress: string | undefined = customerAddresses?.customerAddressesCollection.map(
    (address: ApiModels.GlobalCustomersAddressDto) =>
      address.isPrimary
        ? `${address.streetAddress} ${address.area} ${address.city} ${address.zipcode} ${address.country}`
        : undefined,
  )[0];

  const postAutoship = useCallback(async () => {
    setIsDisabled(true);
    const autoshipInstructionsToPost: ApiModels.GlobalCustomersAutoshipInstructionDto = {
      newConsumer: newCustomer
        ? {
            firstName: newCustomer.firstName,
            lastName: newCustomer.surname,
          }
        : undefined,
      existingConsumerId: customerIdToPost ?? undefined,
      packageId: selectedPackage ? selectedPackage.packageId : 0,
      frequency: Number(selectedFrequency?.Value),
      status: 'Finished',
    };
    try {
      const autoshipInstructionsUrl = getSafeUrl(
        `/customers/${customerId}/autoshipInstructions`,
        globalApiUrl,
      );

      const { data } = await client.post<ApiModels.GlobalCustomersAutoshipInstructionDto>(
        autoshipInstructionsUrl,
        { ...autoshipInstructionsToPost },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-placement': placement,
            'x-referer': uriTemplate,
            'x-tenant-context': tenant,
            authorization: (await token).toString(),
          },
        },
      );

      const detail: EventData = {
        type: EWizardType.Autoship,
        productCode,
        subscriptionId: Number(data.newAutoshipId),
        orderId: data.newOrderId,
        paymentProviderId: data.paymentProviderId,
        paymentId: data.paymentId,
      };
      onSave({ detail });
      onClose();
    } catch (error: unknown) {
      if (error instanceof RestError) {
        const detail: ErrorEventData = {
          type: EWizardType.Autoship,
          error: {
            code: Number(error.response?.status),
            message: String(error.body),
          },
        };
        onError({ detail });
        onClose();
      }
      if (error === 'Missing payment details') {
        const detail: ErrorEventData = {
          type: EWizardType.Autoship,
          error: {
            code: 418,
            message: 'Missing payment details',
          },
        };
        onError({ detail });
        onClose();
      }
    }
  }, [
    globalApiUrl,
    customerId,
    customerIdToPost,
    newCustomer,
    onClose,
    onError,
    onSave,
    placement,
    productCode,
    selectedFrequency?.Value,
    selectedPackage,
    tenant,
    token,
    uriTemplate,
  ]);

  const moveToStep = (stepNumber: number) => () => setCurrentStep(stepNumber);

  const firstRecurringPayment = recurringPaymentCardsData?.collection[0];

  return (
    <ModalContent>
      <ReviewSection
        labelKey="product"
        reviewContent={selectedPackage?.description}
        reviewAction={
          <IconButton
            size="large"
            onClick={moveToStep(0)}
          >
            <PenFilled fontSize="small" />
          </IconButton>
        }
      />
      <ReviewSection
        labelKey="frequency"
        reviewContent={formatMessage(selectedFrequency ? selectedFrequency.Message : {})}
        reviewAction={
          <IconButton
            size="large"
            onClick={moveToStep(1)}
          >
            <PenFilled fontSize="small" />
          </IconButton>
        }
      />
      <ReviewSection
        labelKey="for"
        reviewContent={content}
        reviewAction={
          <IconButton
            size="large"
            onClick={moveToStep(1)}
          >
            <PenFilled fontSize="small" />
          </IconButton>
        }
      />
      <ReviewSection
        labelKey="deliveryAddress"
        reviewContent={customerAddress}
        reviewAction={
          <Tooltip
            leaveTouchDelay={3000}
            enterTouchDelay={0}
            enterDelay={0}
            placement="left"
            title={
              <FormattedMessage
                id="deliveryInformationTooltip"
                defaultMessage={localization.deliveryInformationTooltip}
              />
            }
          >
            <IconButton
              disableFocusRipple
              disableRipple
              size="large"
            >
              <InfoCircle fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <ReviewSection
        labelKey="deliveryMethod"
        reviewContent={
          <FormattedMessage
            id="homeDelivery"
            defaultMessage={localization.homeDelivery}
          />
        }
      />
      {firstRecurringPayment ? (
        <ReviewSection
          labelKey="paymentMethod"
          reviewContent={<CardNumber />}
          reviewAction={
            <div style={{ gridArea: 'review-action' }}>
              {resolveCardImage(firstRecurringPayment.cardType as ECardTypes)}
            </div>
          }
        />
      ) : null}
      {selectedPackage && 'totalPriceForTheFirstStep' in selectedPackage ? (
        <PriceReview
          labelKey="totalCostForThisPeriod"
          price={selectedPackage.totalPriceForTheFirstStep.toString()}
          action={
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={3000}
              enterDelay={0}
              placement="left"
              title={
                <FormattedMessage
                  id="priceTooltip"
                  defaultMessage={localization.priceTooltip}
                />
              }
            >
              <IconButton
                disableFocusRipple
                disableRipple
                size="large"
              >
                <InfoCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
      ) : null}
      <TermsAndConditions
        checked={termsAndConditionsAccepted}
        onChange={setTermsAndConditions}
      >
        <Typography
          variant="caption"
          color="textSecondary"
        >
          <FormattedMessage
            id="termsAndConditions"
            defaultMessage={localization.termsAndConditions}
          />
        </Typography>
        <div>
          <Link
            href={termsAndPrivacyData?.termsAndConditions?.url}
            target="_blank"
          >
            {termsAndPrivacyData?.termsAndConditions?.title}
          </Link>
          <PrivacyPolicy
            href={termsAndPrivacyData?.privacyPolicy?.url}
            target="_blank"
          >
            {termsAndPrivacyData?.privacyPolicy?.title}
          </PrivacyPolicy>
        </div>
      </TermsAndConditions>
      <Button
        fullWidth
        color="primary"
        variant="contained"
        disabled={!termsAndConditionsAccepted || isDisabled}
        onClick={postAutoship}
      >
        <FormattedMessage
          id="payNow"
          defaultMessage={localization.payNow}
        />
      </Button>
    </ModalContent>
  );
}
